import React, { useState } from 'react';
import styles from './SessionTypes.module.css';

function fmt(n) { return `$${Number(n).toFixed(2)}`; }

const ICONS = ['🏅','⚡','👥','🎯','🏋️','🏃','🎽','🧠','💪','🔥','⭐','🥇'];

export default function SessionTypes({ config, refresh, showToast, API, authHeader }) {
  const rates = config?.sessionRates || {};

  const [editing, setEditing]   = useState(null); // key of item being edited
  const [editData, setEditData] = useState({});
  const [showAdd, setShowAdd]   = useState(false);
  const [newType, setNewType]   = useState({ label: '', rate: '', icon: '🏅', active: true, duration: '', allowCustomPrice: false });
  const [saving, setSaving]     = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const startEdit = (key) => {
    setEditing(key);
    setEditData({ ...rates[key] });
    setShowAdd(false);
    setConfirmDelete(false);
  };

  const cancelEdit = () => { setEditing(null); setEditData({}); setConfirmDelete(false); };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/config/session-rates/${editing}`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); showToast(e.error || 'Delete failed', 'error'); return; }
      showToast(`${editData.label || editing} deleted`);
      setEditing(null);
      setConfirmDelete(false);
      refresh();
    } catch { showToast('Network error', 'error'); }
    finally { setSaving(false); }
  };

  const handleSave = async () => {
    if (!editData.label || !editData.rate) { showToast('Label and rate are required', 'error'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/config/session-rates/${editing}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          label: editData.label,
          rate: parseFloat(editData.rate),
          icon: editData.icon || '🏅',
          active: editData.active !== false,
          duration: editData.duration === '' || editData.duration == null ? null : parseInt(editData.duration, 10),
          allowCustomPrice: editData.allowCustomPrice === true,
        })
      });
      if (!res.ok) { const e = await res.json(); showToast(e.error || 'Save failed', 'error'); return; }
      showToast(`${editData.label} updated`);
      setEditing(null);
      refresh();
    } catch { showToast('Network error', 'error'); }
    finally { setSaving(false); }
  };

  const handleAdd = async () => {
    if (!newType.label || !newType.rate) { showToast('Label and rate are required', 'error'); return; }
    setSaving(true);
    try {
      const key = newType.label.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_');
      const res = await fetch(`${API}/config/session-rates/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          label: newType.label,
          rate: parseFloat(newType.rate),
          icon: newType.icon || '🏅',
          active: true,
          duration: newType.duration === '' || newType.duration == null ? null : parseInt(newType.duration, 10),
          allowCustomPrice: newType.allowCustomPrice === true,
        })
      });
      if (!res.ok) { const e = await res.json(); showToast(e.error || 'Add failed', 'error'); return; }
      showToast(`${newType.label} added`);
      setShowAdd(false);
      setNewType({ label: '', rate: '', icon: '🏅', active: true, duration: '', allowCustomPrice: false });
      refresh();
    } catch { showToast('Network error', 'error'); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (key) => {
    const current = rates[key];
    try {
      const res = await fetch(`${API}/config/session-rates/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ ...current, active: !current.active })
      });
      if (!res.ok) { showToast('Update failed', 'error'); return; }
      showToast(`${current.label} ${!current.active ? 'activated' : 'deactivated'}`);
      refresh();
    } catch { showToast('Network error', 'error'); }
  };

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <h1 className={styles.pageTitle}>Session Types</h1>
          <p className={styles.pageSubtitle}>Manage training types and rates used when charging sessions.</p>
        </div>
        <button className={styles.addBtn} onClick={() => { setShowAdd(true); setEditing(null); }}>
          ＋ Add Session Type
        </button>
      </div>

      <div className={styles.grid}>
        {Object.entries(rates).map(([key, r]) => (
          <div key={key} className={`${styles.card} ${!r.active ? styles.inactive : ''}`}>
            {editing === key ? (
              /* ── Edit mode ─────────────────────────────────────────────── */
              <div className={styles.editForm}>
                <div className={styles.iconPicker}>
                  {ICONS.map(icon => (
                    <button key={icon}
                      className={`${styles.iconBtn} ${editData.icon === icon ? styles.iconSelected : ''}`}
                      onClick={() => setEditData(p => ({ ...p, icon }))}>
                      {icon}
                    </button>
                  ))}
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Session Name</label>
                  <input className={styles.fieldInput}
                    value={editData.label || ''}
                    onChange={e => setEditData(p => ({ ...p, label: e.target.value }))}
                    placeholder="e.g. Private Training" />
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Rate (per athlete)</label>
                  <div className={styles.rateInput}>
                    <span className={styles.rateDollar}>$</span>
                    <input className={styles.fieldInput}
                      type="number" min="0" step="5"
                      value={editData.rate || ''}
                      onChange={e => setEditData(p => ({ ...p, rate: e.target.value }))}
                      placeholder="70" />
                  </div>
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Duration (minutes)</label>
                  <input className={styles.fieldInput}
                    type="number" min="0" step="5"
                    value={editData.duration ?? ''}
                    onChange={e => setEditData(p => ({ ...p, duration: e.target.value }))}
                    placeholder="60" />
                </div>
                <label className={styles.allowCustomToggle} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', cursor: 'pointer' }}>
                  <input type="checkbox"
                    checked={editData.allowCustomPrice === true}
                    onChange={e => setEditData(p => ({ ...p, allowCustomPrice: e.target.checked }))}
                    style={{ width: 16, height: 16, marginTop: 2, accentColor: 'var(--orange)' }} />
                  <span style={{ fontSize: '0.85rem', color: 'var(--gray-2)' }}>
                    <strong style={{ color: 'var(--gray-1)' }}>Allow custom price at charge time</strong>
                    <br/>The Charge Session flow will show an editable price (per athlete), pre-filled with the rate above.
                  </span>
                </label>
                <div className={styles.editActions}>
                  <button className={styles.cancelBtn} onClick={cancelEdit}>Cancel</button>
                  <button className={styles.saveBtn} disabled={saving} onClick={handleSave}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
                {!confirmDelete ? (
                  <button className={styles.deleteLink} onClick={() => setConfirmDelete(true)}>
                    Delete Session Type
                  </button>
                ) : (
                  <div className={styles.confirmDelete}>
                    <span className={styles.confirmText}>
                      Permanently delete “{editData.label || editing}”? This can’t be undone.
                    </span>
                    <div className={styles.confirmActions}>
                      <button className={styles.cancelBtn} onClick={() => setConfirmDelete(false)}>Cancel</button>
                      <button className={styles.confirmDeleteBtn} disabled={saving} onClick={handleDelete}>
                        {saving ? 'Deleting...' : 'Confirm Delete'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* ── View mode ─────────────────────────────────────────────── */
              <>
                <div className={styles.cardHeader}>
                  <span className={styles.cardIcon}>{r.icon}</span>
                  <div className={styles.cardMeta}>
                    <div className={styles.cardLabel}>{r.label}</div>
                    <div className={styles.cardKey}>key: {key}</div>
                  </div>
                  {!r.active && <span className={styles.inactiveBadge}>Inactive</span>}
                </div>
                <div className={styles.cardRate}>{fmt(r.rate)}<span className={styles.perAthlete}> / athlete</span></div>
                {r.duration != null && r.duration !== '' && (
                  <div className={styles.cardDuration}>⏱ {r.duration} min</div>
                )}
                {r.allowCustomPrice && (
                  <div className={styles.cardDuration} style={{ color: 'var(--orange)' }}>✎ Custom price at charge time</div>
                )}
                <div className={styles.cardActions}>
                  <button className={styles.editBtn} onClick={() => startEdit(key)}>✎ Edit</button>
                  <button
                    className={`${styles.toggleBtn} ${r.active ? styles.toggleActive : styles.toggleInactive}`}
                    onClick={() => handleToggleActive(key)}>
                    {r.active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </>
            )}
          </div>
        ))}

        {/* ── Add new card ───────────────────────────────────────────────── */}
        {showAdd && (
          <div className={`${styles.card} ${styles.addCard}`}>
            <div className={styles.addCardTitle}>New Session Type</div>
            <div className={styles.iconPicker}>
              {ICONS.map(icon => (
                <button key={icon}
                  className={`${styles.iconBtn} ${newType.icon === icon ? styles.iconSelected : ''}`}
                  onClick={() => setNewType(p => ({ ...p, icon }))}>
                  {icon}
                </button>
              ))}
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Session Name</label>
              <input className={styles.fieldInput}
                value={newType.label}
                onChange={e => setNewType(p => ({ ...p, label: e.target.value }))}
                placeholder="e.g. Speed Training" />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Rate (per athlete)</label>
              <div className={styles.rateInput}>
                <span className={styles.rateDollar}>$</span>
                <input className={styles.fieldInput}
                  type="number" min="0" step="5"
                  value={newType.rate}
                  onChange={e => setNewType(p => ({ ...p, rate: e.target.value }))}
                  placeholder="60" />
              </div>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Duration (minutes)</label>
              <input className={styles.fieldInput}
                type="number" min="0" step="5"
                value={newType.duration}
                onChange={e => setNewType(p => ({ ...p, duration: e.target.value }))}
                placeholder="60" />
            </div>
            <label className={styles.allowCustomToggle} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', cursor: 'pointer' }}>
              <input type="checkbox"
                checked={newType.allowCustomPrice === true}
                onChange={e => setNewType(p => ({ ...p, allowCustomPrice: e.target.checked }))}
                style={{ width: 16, height: 16, marginTop: 2, accentColor: 'var(--orange)' }} />
              <span style={{ fontSize: '0.85rem', color: 'var(--gray-2)' }}>
                <strong style={{ color: 'var(--gray-1)' }}>Allow custom price at charge time</strong>
                <br/>The Charge Session flow will show an editable price (per athlete), pre-filled with the rate above.
              </span>
            </label>
            <div className={styles.editActions}>
              <button className={styles.cancelBtn} onClick={() => setShowAdd(false)}>Cancel</button>
              <button className={styles.saveBtn} disabled={saving} onClick={handleAdd}>
                {saving ? 'Adding...' : 'Add Type'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className={styles.note}>
        <strong>Note:</strong> Deactivating a session type hides it from the Charge Session workflow but preserves all historical transaction records. The rate shown is per athlete — for Pairs training, the total charge is rate × number of athletes.
      </div>
    </div>
  );
}
